import React, { useState } from "react";
import {
  ExternalLink,
  BookOpen,
  Award,
  ChevronDown,
  ChevronUp,
  Search,
} from "lucide-react";

interface DataSource {
  title: string;
  url: string;
  summary: string;
  favicon_url: string | null;
  reliability_score: number;
}

interface WhitePaperDataSourcesProps {
  response: {
    data_sources: DataSource[];
  };
}

const ITEM_LIMIT = 20;

const WhitePaperDataSources: React.FC<WhitePaperDataSourcesProps> = ({
  response,
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterScore, setFilterScore] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<"reliability" | "title">("reliability");

  const toggleAccordion = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const dataSources = response?.data_sources ?? [];

  const filteredAndSortedSources = dataSources
    .filter((source) => {
      const matchesSearch =
        source.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.summary.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter =
        filterScore === null || source.reliability_score >= filterScore;

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortBy === "reliability") {
        return b.reliability_score - a.reliability_score;
      }
      return a.title.localeCompare(b.title);
    });

  // ✅ LIMIT TO 20 ITEMS
  const visibleSources = filteredAndSortedSources.slice(0, ITEM_LIMIT);

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller py-3">
      {/* Header */}
      <div className="mb-6 flex justify-center gap-2 px-2 border-b border-gray-400 pb-3">
        <BookOpen className="w-6 h-6 text-black" />
        <div>
          <h1 className="text-lg font-bold text-gray-900">
            References & Data Sources
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Comprehensive collection of {dataSources.length} verified sources
            supporting the investment management industry analysis
          </p>
        </div>
      </div>

      {/* Accordion List */}
      <div className="grid grid-cols-1 gap-2 px-2">
        {visibleSources.map((source, index) => {
          const isOpen = openIndex === index;
          const displayNumber = dataSources.indexOf(source) + 1;

          return (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full text-left p-3 flex items-start justify-between gap-4 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-100 transition"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 mt-1">
                    {source.favicon_url ? (
                      <img
                        src={source.favicon_url}
                        alt=""
                        className="w-7 h-7 rounded-lg object-cover border border-gray-200"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                        {displayNumber}
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-2">
                      {source.title}
                    </h3>

                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <span className="truncate max-w-xs">
                        {new URL(source.url).hostname}
                      </span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                {isOpen ? (
                  <ChevronUp className="w-3 h-3 text-gray-600 mt-1" />
                ) : (
                  <ChevronDown className="w-3 h-3 text-gray-600 mt-1" />
                )}
              </button>

              {/* Accordion Content */}
              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  isOpen
                    ? "grid-rows-[1fr] opacity-100"
                    : "grid-rows-[0fr] opacity-0"
                }`}
              >
                <div className="overflow-hidden px-4">
                  <p className="text-sm text-gray-700 leading-relaxed my-6">
                    {source.summary}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {visibleSources.length === 0 && (
        <div className="text-center py-16">
          <Search className="w-10 h-10 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No sources found
          </h3>
          <p className="text-gray-600">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default WhitePaperDataSources;
